/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

angular.module('prism-ui.users').service('UsersService', function (this: JS5Object,
                                                                       loginService: JS5Object,
                                                                       ModalService: JS5Object,
                                                                       NotificationService: JS5Object,
                                                                       PermissionsService: JS5Object,
                                                                       UsersResourceFactory: JS5Object,
                                                                       ContentResourceFactory: JS5Object,
                                                                       PaginationService: JS5Object,
                                                                       $q: JS5Object) {
    this.originalEditData = {};
    this.viewData;

    const responseHandler = (data: JS5Object, responseHeaders: JS5Function) => {
        PaginationService.parseResponseHeaders(responseHeaders());
        PaginationService.pageData = data;
    };

    this.getAllUsers = (criteria: JS5Object) => {
        return UsersResourceFactory.query(criteria, responseHandler).$promise
            .then((users: JS5Object[]) => {
                _.forEach(users, (user: JS5Object) => {
                    user.roles = user.roles.rows;
                });
                return users;
            });
    };

    const saveRoles = (user: JS5Object, newRoles: JS5Object[], oldRoles: JS5Object[]) => {
        let rolePromises: JS5Object[] = [];
        let missingRolesById = _.keyBy(oldRoles, 'id');
        _.forEach(newRoles, (role: JS5Object) => {
            if (_.has(missingRolesById, role.id)) {
                delete missingRolesById[role.id];
            } else {
                rolePromises.push(UsersResourceFactory.addUserRole({id: user.id, roleId: role.id}));
            }
        });
        _.forEach(missingRolesById, (role: JS5Object, roleId: string) => {
            rolePromises.push(UsersResourceFactory.removeUserRole({id: user.id, roleId: roleId}));
        });
        return rolePromises;
    };

    const addRolesToUser = (user: JS5Object, roles: JS5Object[]) => {
        // Remove role properties added by Resource API
        user.roles = _.clone(roles);
    }

    this.createUser = (user: JS5Object) => {
        let roles = user.roles;
        return UsersResourceFactory.createUser(_.omit(user, 'roles')).$promise
            .then((savedUser: JS5Object) => {
                return $q.all(saveRoles(savedUser, roles, []))
                    .then(() => {
                        return UsersResourceFactory.getUserRoles({id: savedUser.id}).$promise
                            .then((savedRoles: JS5Object[]) => {
                                addRolesToUser(savedUser, savedRoles);
                                return savedUser;
                            });
                    });
            });
    };

    this.getUser = (userId: number) => {
        return $q.all({
            user: UsersResourceFactory.getUser({id: userId}).$promise,
            roles: UsersResourceFactory.getUserRoles({id: userId}).$promise
        }).then((data: JS5Object) => {
            addRolesToUser(data.user, data.roles);
            return data.user;
        })
    };

    this.updateUser = (user: JS5Object, oldRoles: JS5Object[]) => {
        let promises: JS5Object[] = saveRoles(user, user.roles, oldRoles);
        promises.push(UsersResourceFactory.updateUser(_.omit(user, 'roles')).$promise);
        return $q.all(promises).then((data: JS5Object[]) => {
            let savedUser = data.pop();
            return UsersResourceFactory.getUserRoles({id: savedUser.id}).$promise
                .then((savedRoles: JS5Object[]) => {
                    addRolesToUser(savedUser, savedRoles);
                    return savedUser;
                });
        });
    };

    this.deleteUser = (userId: number) => {
        return UsersResourceFactory.deleteUser({id: userId}).$promise;
    };

    this.openChangePasswordModal = () => {
        PermissionsService.isExternalUser()
            .then((isExternalUser: boolean) => {
                if (isExternalUser) {
                    NotificationService.createFailureNotification(
                        `Cannot change password for an external user`
                    );
                } else {
                    ModalService.showChangePasswordModal(() => {
                    });
                }
            });
    };

    this.showResetPasswordModal = (user: JS5Object) => {
        if (user.externalUser) {
            NotificationService.createFailureNotification(
                `Cannot change password for an external user`
            );
            return;
        }
        ModalService.showMessageModal({
            messageText: `Are you sure you want to send an email to ${user.username} with instructions to reset their password?`,
            pullCancelLeft: true,
            title: 'Reset Password',
            windowClass: 'confirm-modal-sm',
            showOK: true
        }, () => {
            const pendingMessage = `Sending password reset email to ${user.username}`;
            const successMessage = `Successfully sent password reset email to ${user.username}`;
            NotificationService.createInfoNotification(pendingMessage);
            UsersResourceFactory.sendResetPasswordEmail({id: user.id}).$promise
                .then(() => NotificationService.createInfoNotification(successMessage));
        });
    };

    this.getPasswordRequirements = () => {
        return UsersResourceFactory.getPasswordRequirements().$promise;
    };

    this.updatePassword = (oldPassword: JS5Object, newPassword: JS5Object) => {
        return UsersResourceFactory.updatePassword({oldPassword: oldPassword, newPassword: newPassword}).$promise;
    };
});
